/**
 * ManualScanReminderBanner — app-wide nudge to run a manual scan / review on a
 * user-defined cadence (Settings → Trading Preferences → Manual Scan & Review
 * Reminder). Config lives in UserSettings.additionalSettings.manualScanReminder
 * = { enabled, every, unit }. The "next due" slot is tracked in localStorage so
 * it survives reloads and is per-device.
 *
 * Slots are aligned to clock boundaries exactly like candle starts:
 *   nextSlot = floor(now / intervalMs) * intervalMs + intervalMs
 * so "every 4 hours" fires at the 4-hour bucket boundaries (00:00, 04:00 UTC =
 * 05:30, 09:30 IST), never at a random hh:mm. Closing the banner schedules the
 * next slot and the banner re-appears then.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Collapse } from '@mui/material';
import RadarIcon from '@mui/icons-material/Radar';
import { settingsService } from '../../services/settings.service';

type Unit = 'minutes' | 'hours' | 'days';
interface ReminderCfg { enabled: boolean; every: number; unit: Unit; }

// v2 — store the DISMISSED SLOT INDEX rather than a "next due" timestamp. The
// banner shows only once a NEW slot index begins, so dismissing snoozes until
// the next defined slot and is immune to re-renders / remounts / config reloads
// (which previously could re-show it before the next slot).
const SLOT_KEY = 'manualScanReminder.dismissedSlot.v2';
const SIG_KEY = 'manualScanReminder.sig.v2';

function intervalMsOf(cfg: ReminderCfg): number {
  const n = Math.max(1, Math.floor(cfg.every || 1));
  if (cfg.unit === 'minutes') return n * 60_000;
  if (cfg.unit === 'days') return n * 86_400_000;
  return n * 3_600_000; // hours (default)
}

// Slot index = which interval-bucket `now` falls in (aligned to epoch, same
// bucketing the chart uses for candle starts). A new reminder is "due" whenever
// the current slot index exceeds the last dismissed one.
function slotIndexOf(nowMs: number, intervalMs: number): number {
  return Math.floor(nowMs / intervalMs);
}

const ManualScanReminderBanner: React.FC = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState<ReminderCfg | null>(null);
  const [show, setShow] = useState(false);
  const cfgRef = useRef<ReminderCfg | null>(null);
  cfgRef.current = cfg;

  // Load the reminder config from user settings; refresh periodically + on focus
  // so a Settings change is picked up without a full reload.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await settingsService.getUserSettings();
        const s: any = (resp as any)?.data?.data ?? (resp as any)?.data;
        const r = s?.additionalSettings?.manualScanReminder;
        if (cancelled) return;
        if (r && r.enabled) {
          setCfg({ enabled: true, every: Math.max(1, Math.floor(Number(r.every) || 4)), unit: (r.unit as Unit) || 'hours' });
        } else {
          setCfg(null);
        }
      } catch { /* not logged in / transient — ignore */ }
    };
    load();
    const poll = window.setInterval(load, 5 * 60_000);
    const onFocus = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => { cancelled = true; window.clearInterval(poll); document.removeEventListener('visibilitychange', onFocus); };
  }, []);

  // Evaluate "is it due?" on config change, on a 30s tick, and on mount. The
  // banner is due when the current slot index is greater than the last dismissed
  // slot index. On first-enable / cadence change we seed the dismissed slot to
  // the CURRENT slot so it doesn't fire immediately — only at the next slot.
  const evaluate = useCallback(() => {
    const c = cfgRef.current;
    if (!c || !c.enabled) { setShow(false); return; }
    const intervalMs = intervalMsOf(c);
    const cur = slotIndexOf(Date.now(), intervalMs);
    const sig = `${c.every}|${c.unit}`;
    let dismissedSlot = Number(localStorage.getItem(SLOT_KEY));
    const storedSig = localStorage.getItem(SIG_KEY) || '';
    if (storedSig !== sig || !Number.isFinite(dismissedSlot)) {
      dismissedSlot = cur; // seed: snooze the current slot, fire at the next one
      try { localStorage.setItem(SLOT_KEY, String(dismissedSlot)); localStorage.setItem(SIG_KEY, sig); } catch { /* ignore */ }
    }
    setShow(cur > dismissedSlot);
  }, []);

  useEffect(() => {
    evaluate();
    const t = window.setInterval(evaluate, 30_000);
    return () => window.clearInterval(t);
  }, [cfg, evaluate]);

  // Dismiss → record the CURRENT slot as dismissed and hide. The banner won't
  // re-show until a new slot begins. Used by Dismiss / close / Open Strategies.
  const scheduleNext = useCallback(() => {
    const c = cfgRef.current;
    if (c) {
      try {
        localStorage.setItem(SLOT_KEY, String(slotIndexOf(Date.now(), intervalMsOf(c))));
        localStorage.setItem(SIG_KEY, `${c.every}|${c.unit}`);
      } catch { /* ignore */ }
    }
    setShow(false);
  }, []);

  if (!cfg || !cfg.enabled) return null;

  return (
    <Collapse in={show} unmountOnExit>
      <Box sx={{ px: { xs: 1, md: 2 }, pt: 1 }}>
        <Alert
          severity="info"
          icon={<RadarIcon fontSize="small" />}
          onClose={scheduleNext}
          sx={{
            alignItems: 'center',
            // Eye-catching amber→orange gradient so the nudge stands out from
            // the rest of the UI (the default info-blue blended in).
            color: '#2b1700',
            fontWeight: 700,
            border: '1px solid #e65100',
            background: 'linear-gradient(90deg, #ffd54f 0%, #ffb300 45%, #ff7043 100%)',
            boxShadow: '0 2px 12px rgba(255,138,0,0.45)',
            '& .MuiAlert-icon': { color: '#bf360c' },
            '& .MuiAlert-message': { color: '#2b1700' },
            '& .MuiButton-root': { color: '#2b1700', borderColor: 'rgba(43,23,0,0.5)', fontWeight: 700 },
            '& .MuiButton-root:hover': { borderColor: '#2b1700', backgroundColor: 'rgba(43,23,0,0.08)' },
          }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button color="inherit" size="small" onClick={scheduleNext}>
                Dismiss
              </Button>
              <Button color="inherit" size="small" variant="outlined" onClick={() => { navigate('/strategies'); scheduleNext(); }}>
                Open Strategies
              </Button>
            </Box>
          }
        >
          Time for your scheduled <strong>manual scan &amp; review</strong> (every {cfg.every} {cfg.unit}).
          Run a scan and review your alerts. Closing this schedules the next reminder.
        </Alert>
      </Box>
    </Collapse>
  );
};

export default ManualScanReminderBanner;
